export const ACCOUNT_ACTIONS = {
  PROFILE: 'profile',
  ORDERS: 'orders',
  REVIEWS: 'reviews',
  ADDRESSES: 'addresses',
  INVITATIONS: 'invitations',
  PAYMENT_METHODS: 'payment-methods',
  DETAILS: 'details',
  NEWSLETTERS: 'newsletters',
  MILES_AND_MORE: 'miles-and-more'
} as const

export type AccountAction =
  (typeof ACCOUNT_ACTIONS)[keyof typeof ACCOUNT_ACTIONS]

/**
 * Account actions that are no longer nav tabs (removed from v1 in VIP-11837,
 * mirrored in v2 by VIP-12099) but stay reachable through deep links from
 * email footers. They are deliberately absent from ACCOUNT_ACTIONS: adding
 * them there would make TheAccountTemplate treat them as renderable tabs
 * instead of redirecting to profile. The router guard still needs an auth
 * level for them.
 */
export const ACCOUNT_NON_NAV_ACTIONS = {
  NOTIFICATIONS: 'notifications',
  SMS: 'sms'
} as const

export const ACCOUNT_AUTH_LEVELS = {
  /** Legacy `forceVerifiedLogin()`: a soft-logged-in customer must re-authenticate. */
  VERIFIED: 'verified',
  /** Legacy `forceLogin()`: soft login is enough. */
  LOGIN: 'login'
} as const

export type AccountAuthLevel =
  (typeof ACCOUNT_AUTH_LEVELS)[keyof typeof ACCOUNT_AUTH_LEVELS]

/**
 * Auth level per account action, mirroring the legacy controllers: everything
 * calling `forceVerifiedLogin()` in `Customer_Controller_Account_Action` is
 * VERIFIED, the rest only need a (soft) login. Reviews go through
 * `Review_Account_Manage_ReviewController::init()` and the email-module tabs
 * through `Email_Controller_Action::authSubscriber()`, neither of which forces
 * a verified login.
 */
export const ACCOUNT_ACTION_AUTH_LEVELS: Readonly<
  Record<string, AccountAuthLevel>
> = {
  [ACCOUNT_ACTIONS.PROFILE]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.ORDERS]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.ADDRESSES]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.INVITATIONS]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.PAYMENT_METHODS]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.DETAILS]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.MILES_AND_MORE]: ACCOUNT_AUTH_LEVELS.VERIFIED,
  [ACCOUNT_ACTIONS.REVIEWS]: ACCOUNT_AUTH_LEVELS.LOGIN,
  [ACCOUNT_ACTIONS.NEWSLETTERS]: ACCOUNT_AUTH_LEVELS.LOGIN,
  [ACCOUNT_NON_NAV_ACTIONS.NOTIFICATIONS]: ACCOUNT_AUTH_LEVELS.LOGIN,
  [ACCOUNT_NON_NAV_ACTIONS.SMS]: ACCOUNT_AUTH_LEVELS.LOGIN
}

/**
 * Rollout of the account migration (VIP-12098). This one flag decides *who* is
 * in it; what they get is whatever is built, which is
 * `isAccountActionImplemented` below. The monolith redirects the legacy URLs of
 * built tabs once a customer matches, and the same flag reaches us through
 * `/customer/features`, so the nav agrees with the redirect.
 */
export const ACCOUNT_V2_REDIRECT_FLAG = 'account_v2_redirect'

/**
 * Where a tab lives while it is not served from here. A plain link, never an
 * automatic redirect: the monolith sends built tabs back this way, so bouncing
 * would loop the day the two disagree about what is built.
 */
export const legacyAccountUrl = (action: string): string =>
  `/account/manage/${action}`

/**
 * Signals a nav item's visibility can depend on. Kept as one context object so a new gate
 * only adds a field here and reads it in its predicate, the way the nav component fills it
 * once per render.
 */
export interface AccountNavVisibilityContext {
  completedOrdersCount: number
  referralEnabled: boolean
  /** Whether Miles & More is enabled for the current site (SiteConfig, VIP-12085). */
  milesAndMoreEnabled: boolean
}

export interface AccountNavItem {
  action: AccountAction
  labelKey: string
  descriptionKey: string
  /**
   * Optional gate: when present, the item shows only if this returns true. Legacy added the
   * invitations item only for a customer with a completed order on a refer-a-friend site
   * (Customer_Model_Observer::addCustomerNavigationItems, VIP-12084), and the Miles & More
   * item only on sites with it enabled (Milesmore_Model_Observer, VIP-12085); v2 hides each
   * under the same condition rather than linking to a tab the customer was never shown. Items
   * without a predicate always show.
   */
  visibleWhen?: (context: AccountNavVisibilityContext) => boolean
}

export const ACCOUNT_NAV_ITEMS: readonly AccountNavItem[] = [
  {
    action: ACCOUNT_ACTIONS.PROFILE,
    labelKey: 'account_nav_profile',
    descriptionKey: 'account_nav_profile_desc'
  },
  {
    action: ACCOUNT_ACTIONS.ORDERS,
    labelKey: 'account_nav_orders',
    descriptionKey: 'account_nav_orders_desc'
  },
  {
    action: ACCOUNT_ACTIONS.REVIEWS,
    labelKey: 'account_nav_reviews',
    descriptionKey: 'account_nav_reviews_desc'
  },
  {
    action: ACCOUNT_ACTIONS.ADDRESSES,
    labelKey: 'account_nav_addresses',
    descriptionKey: 'account_nav_addresses_desc'
  },
  {
    action: ACCOUNT_ACTIONS.INVITATIONS,
    labelKey: 'account_nav_invitations',
    descriptionKey: 'account_nav_invitations_desc',
    visibleWhen: ({ completedOrdersCount, referralEnabled }) =>
      completedOrdersCount > 0 && referralEnabled
  },
  {
    action: ACCOUNT_ACTIONS.PAYMENT_METHODS,
    labelKey: 'account_nav_payment_methods',
    descriptionKey: 'account_nav_payment_methods_desc'
  },
  {
    action: ACCOUNT_ACTIONS.DETAILS,
    labelKey: 'account_nav_details',
    descriptionKey: 'account_nav_details_desc'
  },
  {
    action: ACCOUNT_ACTIONS.NEWSLETTERS,
    labelKey: 'account_nav_newsletter',
    descriptionKey: 'account_nav_newsletter_desc'
  },
  {
    action: ACCOUNT_ACTIONS.MILES_AND_MORE,
    labelKey: 'account_nav_miles_and_more',
    descriptionKey: 'account_nav_miles_and_more_desc',
    visibleWhen: ({ milesAndMoreEnabled }) => milesAndMoreEnabled
  }
]

export type AccountActionComponentKey =
  | 'placeholder'
  | 'details'
  | 'profile'
  | 'addresses'
  | 'orders'
  | 'reviews'
  | 'newsletters'
  | 'payment-methods'
  | 'invitations'
  | 'miles-and-more'

/**
 * Map of account action -> component key.
 * Used for dynamic content; actual components are resolved in TheAccountTemplate.
 * Replace placeholder keys with specific component keys when implementing sections.
 */
export const ACCOUNT_ACTION_COMPONENT_KEYS: Record<
  AccountAction,
  AccountActionComponentKey
> = {
  [ACCOUNT_ACTIONS.PROFILE]: 'profile',
  [ACCOUNT_ACTIONS.ORDERS]: 'orders',
  [ACCOUNT_ACTIONS.REVIEWS]: 'reviews',
  [ACCOUNT_ACTIONS.ADDRESSES]: 'addresses',
  [ACCOUNT_ACTIONS.INVITATIONS]: 'invitations',
  [ACCOUNT_ACTIONS.PAYMENT_METHODS]: 'payment-methods',
  [ACCOUNT_ACTIONS.DETAILS]: 'details',
  [ACCOUNT_ACTIONS.NEWSLETTERS]: 'newsletters',
  [ACCOUNT_ACTIONS.MILES_AND_MORE]: 'miles-and-more'
}

/**
 * Every lookup by action name has to be an own-property check. A plain object also answers
 * for each `Object.prototype` member, so `/account/manage/v2/constructor` passed as a known
 * action, reported itself as implemented and skipped both the placeholder gate and the
 * verified-login gate in the router (VIP-12098). Nothing narrows the `:action` route param
 * before this, so these are the only checks standing.
 */
export const isKnownAccountAction = (action: unknown): boolean =>
  'string' === typeof action &&
  Object.hasOwn(ACCOUNT_ACTION_AUTH_LEVELS, action)

/** Auth level of an action, null for anything that is not an account action. */
export const accountAuthLevel = (action: unknown): AccountAuthLevel | null =>
  isKnownAccountAction(action)
    ? ACCOUNT_ACTION_AUTH_LEVELS[action as string]
    : null

/** Component key of a renderable tab, null for the non-nav actions and for junk. */
export const accountComponentKey = (
  action: unknown
): AccountActionComponentKey | null =>
  'string' === typeof action &&
  Object.hasOwn(ACCOUNT_ACTION_COMPONENT_KEYS, action)
    ? ACCOUNT_ACTION_COMPONENT_KEYS[action as AccountAction]
    : null

/**
 * Whether a tab has a real component yet. Drives the per-tab rollout: a tab
 * still on the placeholder stays admin-only in the router guard and is hidden
 * from the customer nav, so implementing a tab is enough to release it
 * (VIP-12097).
 */
export const isAccountActionImplemented = (action: string): boolean => {
  const key = accountComponentKey(action)

  return null !== key && key !== 'placeholder'
}
